import { Platform } from 'react-native';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

import {
  getBuildBranch,
  getDatadogApplicationId,
  getDatadogClientToken,
  getDatadogEnabled,
  getDatadogSessionReplayEnabled,
  getVidaEnv,
  isEmbedded,
} from '../../config/runtimeConfig';

// getInitConfiguration() returns undefined before init and the resolved
// config after — the SDK-level source of truth for "has anyone (us or a
// host) already initialized this browser singleton?". More reliable than
// tracking our own module flag, which stays false when a host inits first.
const isDatadogBrowserInited = (): boolean =>
  Boolean(datadogRum.getInitConfiguration() ?? datadogLogs.getInitConfiguration());

export const initDatadog = (): void => {
  // Host owns the singleton under embed — the setGlobalContextProperty
  // block below would relabel the host's telemetry app-wide.
  if (isEmbedded()) return;
  if (!getDatadogEnabled()) return;
  // Safety net: if a host in the same bundle initialized before us without
  // setting `embedded`, still bail rather than clobber their global context.
  if (isDatadogBrowserInited()) return;

  const applicationId = getDatadogApplicationId();
  const clientToken = getDatadogClientToken();
  const service = 'vida-login';
  const application = 'Vida';
  const env = getVidaEnv();

  datadogRum.init({
    applicationId,
    clientToken,
    site: 'datadoghq.com',
    service,
    env,
    version: getBuildBranch(),
    sessionSampleRate: 100,
    sessionReplaySampleRate: getDatadogSessionReplayEnabled() ? 100 : 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask',
  });

  datadogLogs.init({
    clientToken,
    site: 'datadoghq.com',
    service,
    env,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
  });

  datadogRum.setGlobalContextProperty('service', service);
  datadogRum.setGlobalContextProperty('application', application);
  datadogRum.setGlobalContextProperty('platform', 'web');
  datadogLogs.setGlobalContextProperty('service', service);
  datadogLogs.setGlobalContextProperty('application', application);
  datadogLogs.setGlobalContextProperty('platform', 'web');
};

// Under embed on native, the host uses @datadog/mobile-react-native — a
// different singleton than the browser SDK login imports. Writes here would
// silently no-op. Web hosts share the browser SDK, but only if it's actually
// been initialized — writes to an un-inited SDK land in a bounded pre-init
// buffer and are silently dropped. Assert init at the SDK level instead of
// trusting that the host wired it up.
const shouldWriteDatadog = (): boolean => {
  if (isEmbedded()) {
    if (Platform.OS !== 'web') return false;
    return isDatadogBrowserInited();
  }
  return getDatadogEnabled();
};

export const datadogTrackEvent = (name: string, properties?: Record<string, any>): void => {
  if (!shouldWriteDatadog()) return;
  try {
    datadogRum.addAction(name, properties);
  } catch (e) {
    console.error('[Datadog] trackEvent failed', e);
  }
};

// Skipped under embed regardless of who calls it — this file is a public
// subpath export, so a host calling datadogTrackScreen directly under embed
// must NOT reach the standalone getDatadogEnabled() path (which throws on
// missing env vars inside node_modules) or startView() (which would rewrite
// the host's current page-analytics view to a /login/* route name).
export const datadogTrackScreen = (name: string): void => {
  if (isEmbedded()) return;
  if (!getDatadogEnabled()) return;
  try {
    datadogRum.startView({ name });
  } catch (e) {
    console.error('[Datadog] trackScreen failed', e);
  }
};

export const datadogTrackError = (error: Error | string, context?: Record<string, any>): void => {
  if (!shouldWriteDatadog()) return;
  try {
    const err = error instanceof Error ? error : new Error(error);
    datadogRum.addError(err, context);
    datadogLogs.logger.error(err.message, context);
  } catch (e) {
    console.error('[Datadog] trackError failed', e);
  }
};
